import React, { useEffect, useState } from 'react';
import styles from '../../styles/dashboard.module.css';
import camStyles from './cameras.module.css';
import Header from '../../components/header/header';
import PageTitle from '../../components/page_title/page_title';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faVideo, faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../AxiosInstance';
import CameraManager from '../../components/camera_viewer/camera_manager';
import CameraViewer from '../../components/camera_viewer/camera_viewer';
import { Modal as BootStrapModal } from 'react-bootstrap';
import PaginationControls, { getStoredPageSize } from '../../components/pagination/PaginationControls';

export default function Cameras() {
    const { t } = useTranslation();
    const project_id = parseInt(localStorage.getItem('project_id'));

    const [cameras, setCameras] = useState([]);
    const [search, setSearch] = useState('');
    const [deviceUsers, setDeviceUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(getStoredPageSize);

    // Add/Edit modal
    const [showManager, setShowManager] = useState(false);
    const [editCamera, setEditCamera] = useState(null);

    // View stream modal
    const [viewCamera, setViewCamera] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        setPage(1);
    }, [search]);

    const fetchData = () => {
        setLoading(true);
        Promise.all([
            axiosInstance.get(`ip_camera/?project_id=${project_id}`),
            axiosInstance.get(`device_user/?project_id=${project_id}`),
        ]).then(([camRes, duRes]) => {
            setCameras(Array.isArray(camRes.data) ? camRes.data : (camRes.data.results || []));
            setDeviceUsers(Array.isArray(duRes.data) ? duRes.data : (duRes.data.results || []));
            setLoading(false);
        }).catch(() => setLoading(false));
    };

    const openAdd = () => {
        setEditCamera(null);
        setShowManager(true);
    };

    const openEdit = (cam) => {
        setEditCamera(cam);
        setShowManager(true);
    };

    const handleSaved = () => {
        fetchData();
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('camera.confirmDelete'))) return;
        await axiosInstance.delete(`ip_camera/${id}/`);
        fetchData();
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const getPlacement = (cam) => {
        if (cam.has_map_placement) return t('camera.placementOutdoor');
        if (cam.has_floor_placement) return t('camera.placementIndoor');
        return t('camera.placementNone');
    };

    const handlePageSizeChange = (newSize) => { setPageSize(newSize); setPage(1); };

    const filteredCameras = cameras.filter(c => c.name?.toLowerCase().includes(search.toLowerCase()));
    const totalPages = Math.ceil(filteredCameras.length / pageSize);
    const pagedCameras = filteredCameras.slice((page - 1) * pageSize, page * pageSize);

    return (
        <div className={styles.dashboard_container}>
            <Header sidebarIndex={-1} />

            {showManager && (
                <CameraManager
                    show={showManager}
                    setter={setShowManager}
                    camera={editCamera}
                    projectId={project_id}
                    deviceUserId={null}
                    deviceUsers={deviceUsers}
                    onSaved={handleSaved}
                />
            )}

            <BootStrapModal show={!!viewCamera} onHide={() => setViewCamera(null)} size="lg" centered>
                <BootStrapModal.Header closeButton>
                    <BootStrapModal.Title style={{ fontSize: 16 }}>
                        <FontAwesomeIcon icon={faVideo} className="me-2" />
                        {viewCamera?.name}
                    </BootStrapModal.Title>
                </BootStrapModal.Header>
                <BootStrapModal.Body>
                    {viewCamera && <CameraViewer cameraId={viewCamera.id} name={viewCamera.name} camera={viewCamera} />}
                </BootStrapModal.Body>
            </BootStrapModal>

            <div className={styles.dashboardInnerContainer}>
                <div className={camStyles.titleRow}>
                    <PageTitle title={t('camera.cameras')} />
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <div style={{ position: 'relative' }}>
                            <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', fontSize: '13px', pointerEvents: 'none' }} />
                            <input
                                type="text"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder={t('common.search') + '...'}
                                style={{ paddingLeft: '30px', paddingRight: '10px', height: '32px', border: '1px solid #CBD5E1', borderRadius: '6px', fontSize: '13px', outline: 'none', width: '200px' }}
                            />
                        </div>
                        <button className="btn btn-primary btn-sm" onClick={openAdd}>
                            <FontAwesomeIcon icon={faPlus} className="me-1" /> {t('camera.addCamera')}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className={camStyles.emptyState}>{t('common.loading')}</div>
                ) : cameras.length === 0 ? (
                    <div className={camStyles.emptyState}>{t('camera.noCamerasProject')}</div>
                ) : (<>
                    {/* Desktop table */}
                    <div className={camStyles.tableWrapper}>
                        <table className={camStyles.table}>
                            <thead>
                                <tr>
                                    <th>{t('common.name')}</th>
                                    <th>{t('camera.machine')}</th>
                                    <th>{t('camera.placement')}</th>
                                    <th>{t('camera.host')}</th>
                                    <th>{t('camera.updatedCol')}</th>
                                    <th>{t('common.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedCameras.map(cam => (
                                    <tr key={cam.id}>
                                        <td style={{ fontWeight: 500 }}>{cam.name}</td>
                                        <td>{cam.device_user_name || <span className={camStyles.noMachineBadge}>{t('camera.noMachine')}</span>}</td>
                                        <td>
                                            <span className={
                                                cam.has_map_placement ? camStyles.badgeOutdoor :
                                                cam.has_floor_placement ? camStyles.badgeIndoor :
                                                camStyles.badgeNone
                                            }>
                                                {getPlacement(cam)}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: 13, color: '#64748B' }}>{cam.onvif_host || cam.rtsp_url || '-'}</td>
                                        <td>{formatDate(cam.updated_at)}</td>
                                        <td>
                                            <button className={camStyles.actionBtn} onClick={() => setViewCamera(cam)} title={t('camera.viewStream')}>
                                                <FontAwesomeIcon icon={faVideo} />
                                            </button>
                                            <button className={camStyles.actionBtn} onClick={() => openEdit(cam)} title={t('common.edit')}>
                                                <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                            <button className={`${camStyles.actionBtn} ${camStyles.actionBtnDanger}`} onClick={() => handleDelete(cam.id)} title={t('common.delete')}>
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className={camStyles.mobileCards}>
                        {pagedCameras.map(cam => (
                            <div key={cam.id} className={camStyles.camCard}>
                                <div className={camStyles.camCardHeader}>
                                    <div className={camStyles.camCardName}>{cam.name}</div>
                                    <span className={
                                        cam.has_map_placement ? camStyles.badgeOutdoor :
                                        cam.has_floor_placement ? camStyles.badgeIndoor :
                                        camStyles.badgeNone
                                    }>
                                        {getPlacement(cam)}
                                    </span>
                                </div>
                                <div className={camStyles.camCardMeta}>
                                    {cam.device_user_name && <span><b>{t('camera.machine')}:</b> {cam.device_user_name}</span>}
                                    {(cam.onvif_host || cam.rtsp_url) && <span><b>{t('camera.host')}:</b> {cam.onvif_host || cam.rtsp_url}</span>}
                                    <span><b>{t('camera.updatedCol')}:</b> {formatDate(cam.updated_at)}</span>
                                </div>
                                <div className={camStyles.camCardActions}>
                                    <button className={camStyles.actionBtn} onClick={() => setViewCamera(cam)}>
                                        <FontAwesomeIcon icon={faVideo} /> {t('camera.viewStream')}
                                    </button>
                                    <button className={camStyles.actionBtn} onClick={() => openEdit(cam)}>
                                        <FontAwesomeIcon icon={faEdit} /> {t('common.edit')}
                                    </button>
                                    <button className={`${camStyles.actionBtn} ${camStyles.actionBtnDanger}`} onClick={() => handleDelete(cam.id)}>
                                        <FontAwesomeIcon icon={faTrash} /> {t('common.delete')}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    <PaginationControls
                        page={page}
                        totalPages={totalPages}
                        pageSize={pageSize}
                        onPageChange={setPage}
                        onPageSizeChange={handlePageSizeChange}
                        totalCount={filteredCameras.length}
                    />
                </>)}
            </div>
        </div>
    );
}
